import { 
    SupportClient, 
    DescribeTrustedAdvisorChecksCommand,
    DescribeTrustedAdvisorCheckSummariesCommand
} from '@aws-sdk/client-support';

import { requestQueueManager } from './requestQueueManager.js';

const createSupportClient = (credentials, region) => {
  return new SupportClient({
    region: 'us-east-1', // Support API is only available in us-east-1
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    retryMode: 'adaptive', // SDK adaptive retry adds a client-side rate limiter as a backstop
    maxAttempts: 2,        // bounded retry; RequestQueueManager's token bucket is the primary pacing
    requestHandler: {
      requestTimeout: 15000, // Reduced timeout to prevent hanging on rate limits
    }
  });
};

export const fetchTrustedAdvisorRecommendations = async (credentials, region) => {
  const client = createSupportClient(credentials, region);

  try {
    // First, get the list of available Trusted Advisor checks
    const checksResponse = await requestQueueManager.executeRequest(
      async () => {
        const command = new DescribeTrustedAdvisorChecksCommand({
          language: 'en'
        });
        return client.send(command);
      },
      { resourceType: 'trusted-advisor-checks' }
    );

    const checks = checksResponse.checks || [];

    // Get summaries for all checks
    const checkIds = checks.map(check => check.id);
    const summariesResponse = await requestQueueManager.executeRequest(
      async () => {
        const command = new DescribeTrustedAdvisorCheckSummariesCommand({
          checkIds: checkIds
        });
        return client.send(command);
      },
      { resourceType: 'trusted-advisor-summaries' }
    );

    const summaries = summariesResponse.summaries || [];

    // Process the data to format it for Excel export
    const processedRecommendations = [];

    for (const summary of summaries) {
      try {
        // Find the corresponding check details
        const checkDetail = checks.find(check => check.id === summary.checkId);
        
        if (!checkDetail) continue;

        const processedRec = {
          Name: checkDetail.name || '',
          Description: checkDetail.description || '',
          Category: formatCategory(checkDetail.category),
          Status: formatStatus(summary.status),
          Type: 'Standard', // Trusted Advisor checks are typically "Standard"
          PotentialMonthlySavings: '', // Will be populated for cost optimization checks
          ResourcesFlagged: summary.resourcesSummary?.resourcesFlagged || 0,
          ResourcesIgnored: summary.resourcesSummary?.resourcesIgnored || 0,
          ResourcesSuppressed: summary.resourcesSummary?.resourcesSuppressed || 0,
          ResourcesProcessed: summary.resourcesSummary?.resourcesProcessed || 0,
          LastUpdated: formatDate(summary.timestamp),
          Arn: summary.checkId || ''
        };


        // For cost optimization checks, extract potential savings from categorySpecificSummary
        if (checkDetail.category && checkDetail.category === 'cost_optimizing') {
          // Extract from categorySpecificSummary (authoritative source for cost savings)
          if (summary.categorySpecificSummary && summary.categorySpecificSummary.costOptimizing) {
            const costData = summary.categorySpecificSummary.costOptimizing;
            
            if (costData.estimatedMonthlySavings && costData.estimatedMonthlySavings > 0) {
              processedRec.PotentialMonthlySavings = `$${parseFloat(costData.estimatedMonthlySavings).toFixed(2)}`;
            }
          }
        }

        processedRecommendations.push(processedRec);

      } catch (error) {
        console.warn(`Failed to process check ${summary.checkId}:`, error.message);
      }
    }

    return processedRecommendations;

  } catch (error) {
    console.error('Failed to fetch Trusted Advisor recommendations:', error);
    
    // Check if this is a permissions issue (common for accounts without Business/Enterprise support)
    if (error.name === 'SubscriptionRequiredException' || error.message.includes('SubscriptionRequired')) {
      throw new Error('Trusted Advisor requires AWS Business or Enterprise support plan. Please upgrade your support plan or disable Trusted Advisor export.');
    }
    
    throw error;
  }
};

// Helper functions to format data for display
const formatCategory = (category) => {
  // Trusted Advisor categories include: cost_optimizing, security, fault_tolerance, performance, service_limits
  const categoryNames = {
    'cost_optimizing': 'Cost Optimization',
    'performance': 'Performance',
    'security': 'Security', 
    'fault_tolerance': 'Fault Tolerance',
    'service_limits': 'Service Limits'
  };
  return categoryNames[category] || category;
};

const formatStatus = (status) => {
  // Traditional Support API uses: error, warning, ok, not_available
  const statusNames = {
    'error': 'Action Recommended',
    'warning': 'Investigation Recommended', 
    'ok': 'No Issues Found',
    'not_available': 'Check Not Available'
  };
  return statusNames[status] || status;
};


const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return dateString;
  }
};